import { defineStore } from 'pinia';

interface OrderLine {
  orderId: string;
  product: {
    id: string;
    name: { [key: string]: string };
    price: number;
    category: {
      name: { [key: string]: string };
    };
    [key: string]: any;
  };
  supplements: any[];
  price: number;
  quantity?: number;
}

interface CateringOrder {
  id: string;
  eventId: string;
  userId: string | null;
  customerNote: string;
  delivery: boolean;
  deliveryLocation: string | null;
  status: string;
  orderNumber: string;
  totalPrice: number;
  orderLines: OrderLine[];
  createdAt: string;
  updatedAt: string;
}

export const useCateringOpenOrdersStore = defineStore('cateringOpenOrders', {
  state: () => ({
    openOrders: [] as CateringOrder[],
  }),

  getters: {
    getOrderById: (state) => (orderId: string) => {
      return state.openOrders.find(order => order.id === orderId);
    },
    getAllOrders: (state) => state.openOrders,
    sortedOpenOrders: (state) => {
      return [...state.openOrders].sort((a, b) => b.orderNumber - a.orderNumber);
    },
  },

  actions: {
    addOrder(order: CateringOrder) {
      const processedOrder = this.processOrder(order);
      this.openOrders.push(processedOrder);
    },

    updateOrder(updatedOrder: CateringOrder) {
      const processedOrder = this.processOrder(updatedOrder);
      const index = this.openOrders.findIndex(order => order.id === processedOrder.id);
      if (index !== -1) {
        this.openOrders[index] = processedOrder;
      }
    },

    removeOrder(orderId: string) {
      this.openOrders = this.openOrders.filter(order => order.id !== orderId);
    },

    setOrders(orders: CateringOrder[]) {
      // Keep track of previous orders to check for status changes
      const previousOrders = this.openOrders;
      const newOrders = orders.map(order => this.processOrder(order));
      
      // Check for completed orders
      newOrders.forEach(newOrder => {
        const previousOrder = previousOrders.find(order => order.id === newOrder.id);
        if (previousOrder && previousOrder.status !== 'COMPLETED' && newOrder.status === 'COMPLETED') {
          this.notifyOrderCompleted(newOrder);
        }
      });
      
      this.openOrders = newOrders;
    },

    notifyOrderCompleted(order: CateringOrder) {
      // Check if notifications are supported and permission is granted
      if (!("Notification" in window)) {
        return;
      }

      // Request permission if needed
      if (Notification.permission === "default") {
        Notification.requestPermission();
      }

      // Show notification if permitted
      if (Notification.permission === "granted") {
        if (order.delivery === false) {
          new Notification("Order is ready!", {
            body: `Your order #${order.orderNumber} is ready for pickup!`,
            icon: "/favicon.ico", // You can add an icon path here
            tag: `order-${order.id}`, // Prevents duplicate notifications
            requireInteraction: true // Notification persists until user interacts with it
          });
        } else {
          new Notification("Order is ready!", {
            body: `Your order #${order.orderNumber} is on it's way to you!`,
            icon: "/favicon.ico", // You can add an icon path here
            tag: `order-${order.id}`, // Prevents duplicate notifications
            requireInteraction: true // Notification persists until user interacts with it
          });
        }
      }
    },

    processOrder(order: CateringOrder): CateringOrder {
      const combinedOrderLines: OrderLine[] = [];
      
      order.orderLines.forEach((orderLine: OrderLine) => {
        const existingOrderLine = combinedOrderLines.find(
          (ol: OrderLine) => ol.product.id === orderLine.product.id && 
                            JSON.stringify(ol.supplements || []) === JSON.stringify(orderLine.supplements || [])
        );
        
        if (existingOrderLine) {
          existingOrderLine.quantity = Number(existingOrderLine.quantity || 1) + 1;
        } else {
          combinedOrderLines.push({ 
            ...orderLine,
            quantity: 1
          });
        }
      });

      // Sort by category (beverages first)
      combinedOrderLines.sort((a, b) => {
        const aIsBeverage = a.product.category.name.en === "Beverages";
        const bIsBeverage = b.product.category.name.en === "Beverages";
        if (aIsBeverage && !bIsBeverage) return -1;
        if (!aIsBeverage && bIsBeverage) return 1;
        return 0;
      });
      
      return {
        ...order,
        orderLines: combinedOrderLines
      };
    }
  },
});
